/**
 * CrmLeadDrawer — V2.1.0 (Feb 2026, Phase 1b)
 *
 * Right-side drawer showing full lead detail, outreach/disposition
 * history, and the two promotion actions: "Upload CV" (recruiter
 * path) and "Register on behalf" (self-register mock for now).
 * Backed by GET /api/crm/leads/{lid} and POST /api/crm/leads/{lid}/promote.
 */
import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { toast } from "sonner";
import { WhatsAppLoginLinkButton } from "@/components/WhatsAppLoginLinkButton";
import {
    X, UploadSimple, ArrowRight, Clock, ThumbsUp, Envelope, Phone,
    MapPin, Briefcase, FileText, UserPlus, Lightning, ListChecks,
} from "@phosphor-icons/react";

function _fmt(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }); }
    catch { return iso; }
}

export default function CrmLeadDrawer({ leadId, open, onClose, onPromoted, onDispositionRequest }) {
    const [lead, setLead] = useState(null);
    const [disps, setDisps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [quickBusy, setQuickBusy] = useState(false);
    // V2.1.1 iter184 — Kanban Journey (only populated for
    // source_batch=kanban_reject leads).
    const [journey, setJourney] = useState(null);

    const fetchOne = useCallback(() => {
        if (!leadId) return;
        setLoading(true);
        setJourney(null);
        api.get(`/crm/leads/${leadId}`)
            .then((r) => {
                setLead(r.data.lead);
                setDisps(r.data.dispositions || []);
                if (r.data.lead?.source_batch === "kanban_reject") {
                    api.get(`/crm/leads/${leadId}/journey`)
                        .then((jr) => setJourney(jr.data?.journey || null))
                        .catch(() => {});
                }
            })
            .catch((e) => { logError("CRM lead fetch:", e); toast.error("Couldn't load lead"); })
            .finally(() => setLoading(false));
    }, [leadId]);

    async function quickDisposition(code) {
        if (!leadId || quickBusy) return;
        setQuickBusy(true);
        try {
            await api.post("/crm/dispositions", {
                target_type: "crm_lead", target_id: leadId, code,
            });
            toast.success(`Marked as ${code.replace(/_/g, " ")}`);
            fetchOne();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save");
        } finally { setQuickBusy(false); }
    }

    useEffect(() => { if (open) fetchOne(); }, [open, fetchOne]);

    if (!open) return null;

    async function onUploadCv(e) {
        const file = e.target.files?.[0];
        if (!file || !leadId) return;
        setUploading(true);
        try {
            // Step 1 — promote (idempotent). Produces the candidate id.
            const p = await api.post(`/crm/leads/${leadId}/promote`);
            const candidateId = p.data?.candidate?.id;
            if (!candidateId) throw new Error("Promotion did not return a candidate id");
            // Step 2 — upload the CV to the new candidate row via the
            // existing multipart endpoint. This runs the CV LLM parse
            // + signals refresh automatically.
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/candidates/${candidateId}/resume`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            // Reflect CV state on the CRM lead so the drawer + list update.
            try {
                await api.patch(`/crm/leads/${leadId}`, { has_cv: true }).catch(() => {});
            } catch { /* endpoint optional in Phase 1 */ }
            toast.success(p.data?.already_promoted ? "CV attached to existing candidate" : "Promoted + CV uploaded");
            onPromoted?.(p.data.candidate);
            fetchOne();
        } catch (err) {
            logError("CV upload failed:", err);
            toast.error(err?.response?.data?.detail || "CV upload failed");
        } finally { setUploading(false); e.target.value = ""; }
    }

    async function onRegisterOnBehalf() {
        if (!leadId) return;
        // Phase 1b — flag lead as "self-registered" and promote without CV.
        // In Phase 2 we'll send a real magic-link + OTP flow.
        try {
            const p = await api.post(`/crm/leads/${leadId}/promote`);
            toast.success("Promoted (no CV yet — candidate can upload later)");
            onPromoted?.(p.data.candidate);
            fetchOne();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Promotion failed");
        }
    }

    const promoted = !!lead?.promoted_to_candidate_id;

    return (
        <div
            data-testid="crm-lead-drawer"
            className="fixed inset-0 z-40 bg-ink/50"
            onClick={onClose}
        >
            <aside
                className="absolute right-0 top-0 h-full w-full max-w-lg bg-white border-l-2 border-ink overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="sticky top-0 bg-white border-b-2 border-ink px-5 py-4 flex items-start justify-between z-10">
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                            CRM Lead {promoted && "· PROMOTED"}
                        </p>
                        <h2 className="font-heading font-black text-2xl leading-none mt-1" data-testid="crm-drawer-name">
                            {loading ? "…" : (lead?.name || "—")}
                        </h2>
                    </div>
                    <button onClick={onClose} data-testid="crm-drawer-close" className="p-1 hover:bg-neutral-100">
                        <X size={18} weight="bold" />
                    </button>
                </div>

                {loading || !lead ? (
                    <div className="px-5 py-8 text-sm text-neutral-500">Loading…</div>
                ) : (
                    <div className="px-5 py-5 space-y-5">
                        {/* Contact */}
                        <section>
                            <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Contact</p>
                            <div className="space-y-1 text-sm">
                                {lead.email && <div className="flex items-center gap-2"><Envelope size={14} /> {lead.email}</div>}
                                {lead.phone && <div className="flex items-center gap-2"><Phone size={14} /> {lead.phone}</div>}
                                {lead.city && <div className="flex items-center gap-2"><MapPin size={14} /> {lead.city}</div>}
                                {lead.current_title && <div className="flex items-center gap-2"><Briefcase size={14} /> {lead.current_title}{lead.years_experience ? ` · ${lead.years_experience}y` : ""}</div>}
                            </div>
                        </section>

                        {/* Skills */}
                        {(lead.skills || []).length > 0 && (
                            <section>
                                <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Skills</p>
                                <div className="flex flex-wrap gap-1">
                                    {lead.skills.map((s) => (
                                        <span key={s} className="text-[10px] font-bold uppercase tracking-widest border border-neutral-300 px-2 py-0.5">{s}</span>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* V2.1.1 iter184 — Previous Kanban Journey card,
                            shown only for auto-recycled leads. */}
                        {journey && (
                            <section
                                data-testid="crm-drawer-journey"
                                className="border-2 border-cobalt bg-cobalt/5 p-4"
                            >
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="text-[10px] font-mono uppercase tracking-widest text-cobalt font-black">
                                        Previous Kanban journey
                                    </span>
                                </div>
                                <div className="space-y-2 text-xs">
                                    {journey.previous_job_title && (
                                        <div>
                                            <p className="font-mono uppercase text-[9px] tracking-widest text-neutral-500">Last job</p>
                                            <p className="font-bold text-sm text-ink" data-testid="crm-drawer-prev-job">
                                                {journey.previous_job_title}
                                                {journey.previous_client_name && (
                                                    <span className="text-neutral-500"> · {journey.previous_client_name}</span>
                                                )}
                                            </p>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-3 gap-2 pt-1">
                                        <div>
                                            <p className="font-mono uppercase text-[9px] tracking-widest text-neutral-500">Last stage</p>
                                            <p className="font-bold text-xs mt-0.5">{journey.previous_stage || "—"}</p>
                                        </div>
                                        <div>
                                            <p className="font-mono uppercase text-[9px] tracking-widest text-neutral-500">Reminders</p>
                                            <p className="font-bold text-xs mt-0.5">{journey.reminders_sent_before_reject ?? 0}</p>
                                        </div>
                                        <div>
                                            <p className="font-mono uppercase text-[9px] tracking-widest text-neutral-500">Rejected</p>
                                            <p className="font-bold text-xs mt-0.5">{_fmt(journey.reject_at)}</p>
                                        </div>
                                    </div>
                                    {journey.reject_reason && (
                                        <div className="pt-1">
                                            <p className="font-mono uppercase text-[9px] tracking-widest text-neutral-500">Reason</p>
                                            <p className="text-xs mt-0.5">
                                                {String(journey.reject_reason).replace(/_/g, " ")}
                                            </p>
                                        </div>
                                    )}
                                    {(journey.previous_stage_history || []).length > 0 && (
                                        <details className="pt-2">
                                            <summary className="cursor-pointer text-cobalt font-bold uppercase text-[10px] tracking-widest">
                                                Stage timeline ({journey.previous_stage_history.length})
                                            </summary>
                                            <ul className="mt-2 space-y-1 pl-2 border-l-2 border-cobalt/40">
                                                {journey.previous_stage_history.slice(-6).map((h, i) => (
                                                    <li key={i} className="text-[11px] text-neutral-700">
                                                        <span className="font-bold">{h.stage}</span>
                                                        <span className="text-neutral-500"> · {_fmt(h.at)}</span>
                                                        {h.reason && (
                                                            <span className="text-neutral-500"> — {h.reason}</span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </details>
                                    )}
                                </div>
                            </section>
                        )}

                        {/* Progress */}
                        <section className="border border-ink p-3">
                            <div className="grid grid-cols-3 gap-2 text-center">
                                <div>
                                    <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Attempts</p>
                                    <p className="font-heading font-black text-xl">{lead.attempt_count || 0}/3</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Has CV</p>
                                    <p className="font-heading font-black text-xl">{lead.has_cv ? "✓" : "—"}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Source</p>
                                    <p className="font-bold text-xs mt-1">{lead.source_batch || "manual"}</p>
                                </div>
                            </div>
                        </section>

                        {/* Actions — V2.1.0 iter167 conversion-first order */}
                        <section className="space-y-2">
                            <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Convert this lead</p>
                            {/* Row 1 — primary contact outcome */}
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    onClick={() => quickDisposition("reachable")}
                                    disabled={promoted || quickBusy}
                                    data-testid="crm-drawer-reachable"
                                    className="border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-40"
                                >
                                    <ThumbsUp size={14} weight="bold" /> Reachable
                                </button>
                                <button
                                    onClick={() => quickDisposition("not_reachable")}
                                    disabled={promoted || quickBusy}
                                    data-testid="crm-drawer-not-reachable"
                                    className="border-2 border-amber-500 text-amber-700 hover:bg-amber-50 px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-40"
                                >
                                    Not reachable (+1)
                                </button>
                            </div>
                            <WhatsAppLoginLinkButton lead={lead} disabled={promoted} onSent={fetchOne} />
                            {/* Row 2 — nudge candidate */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <button
                                    onClick={() => quickDisposition("magic_link_sent")}
                                    disabled={promoted || quickBusy || !lead.email}
                                    data-testid="crm-drawer-send-magic-link"
                                    className="bg-cobalt text-white hover:bg-ink px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-40"
                                    title={!lead.email ? "Needs an email to send the magic link" : ""}
                                >
                                    <Lightning size={14} weight="bold" /> Send magic link
                                </button>
                                <label className={`border-2 border-cobalt text-cobalt px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-cobalt/5 flex items-center justify-center gap-2 cursor-pointer ${promoted ? "opacity-40 pointer-events-none" : ""}`}>
                                    <UploadSimple size={14} weight="bold" /> {uploading ? "Uploading…" : "Upload CV → Promote"}
                                    <input type="file" accept="application/pdf" hidden onChange={onUploadCv} data-testid="crm-drawer-upload-cv" />
                                </label>
                            </div>
                            {/* Row 3 — auxiliary */}
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    onClick={() => onDispositionRequest?.(lead.id)}
                                    data-testid="crm-drawer-disposition"
                                    className="border-2 border-ink px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-neutral-100 flex items-center justify-center gap-2"
                                >
                                    <ListChecks size={14} weight="bold" /> More statuses
                                </button>
                                <button
                                    onClick={onRegisterOnBehalf}
                                    disabled={promoted}
                                    data-testid="crm-drawer-promote-noreg"
                                    className="border-2 border-neutral-400 text-neutral-700 hover:bg-neutral-100 px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-40"
                                >
                                    <UserPlus size={14} weight="bold" /> Promote (no CV)
                                </button>
                            </div>
                            {promoted && (
                                <p className="text-[11px] text-emerald-700 font-bold" data-testid="crm-drawer-promoted-note">
                                    ✓ Promoted to Candidate Pool · id {lead.promoted_to_candidate_id?.slice(0, 8)}…
                                </p>
                            )}
                        </section>

                        {/* Disposition history */}
                        <section>
                            <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Disposition history</p>
                            {disps.length === 0 ? (
                                <p className="text-xs text-neutral-500">No dispositions yet.</p>
                            ) : (
                                <ul className="space-y-2" data-testid="crm-drawer-disp-history">
                                    {disps.map((d) => (
                                        <li key={d.id} className="border-l-2 border-neutral-300 pl-3 py-1">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-bold capitalize">{d.code.replace(/_/g, " ")}</span>
                                                <span className="text-[10px] text-neutral-400">by {d.by_name || d.by_role}</span>
                                                <span className="text-[10px] text-neutral-400 ml-auto"><Clock size={10} className="inline" /> {_fmt(d.at)}</span>
                                            </div>
                                            {d.reason_notes && <p className="text-xs text-neutral-600 mt-0.5">{d.reason_notes}</p>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>

                        {/* Notes */}
                        {lead.notes && (
                            <section>
                                <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Notes</p>
                                <p className="text-sm text-neutral-700 border-l-2 border-neutral-300 pl-3">{lead.notes}</p>
                            </section>
                        )}
                    </div>
                )}
            </aside>
        </div>
    );
}
